/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from "@solana/program-client-core";
import { findEventAuthorityPda } from "../pdas/index.js";
import { CP_AMM_PROGRAM_ADDRESS } from "../programs/index.js";

export const SPLIT_POSITION2_DISCRIMINATOR: ReadonlyUint8Array = new Uint8Array(
  [221, 147, 228, 207, 140, 212, 17, 119],
);

export function getSplitPosition2DiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SPLIT_POSITION2_DISCRIMINATOR,
  );
}

export type SplitPosition2Instruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountFirstPosition extends string | AccountMeta<string> = string,
  TAccountFirstPositionNftAccount extends string | AccountMeta<string> = string,
  TAccountSecondPosition extends string | AccountMeta<string> = string,
  TAccountSecondPositionNftAccount extends string | AccountMeta<string> =
    string,
  TAccountFirstOwner extends string | AccountMeta<string> = string,
  TAccountSecondOwner extends string | AccountMeta<string> = string,
  TAccountEventAuthority extends string | AccountMeta<string> = string,
  TAccountProgram extends string | AccountMeta<string> =
    "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountFirstPosition extends string
        ? WritableAccount<TAccountFirstPosition>
        : TAccountFirstPosition,
      TAccountFirstPositionNftAccount extends string
        ? ReadonlyAccount<TAccountFirstPositionNftAccount>
        : TAccountFirstPositionNftAccount,
      TAccountSecondPosition extends string
        ? WritableAccount<TAccountSecondPosition>
        : TAccountSecondPosition,
      TAccountSecondPositionNftAccount extends string
        ? ReadonlyAccount<TAccountSecondPositionNftAccount>
        : TAccountSecondPositionNftAccount,
      TAccountFirstOwner extends string
        ? ReadonlySignerAccount<TAccountFirstOwner> &
            AccountSignerMeta<TAccountFirstOwner>
        : TAccountFirstOwner,
      TAccountSecondOwner extends string
        ? ReadonlySignerAccount<TAccountSecondOwner> &
            AccountSignerMeta<TAccountSecondOwner>
        : TAccountSecondOwner,
      TAccountEventAuthority extends string
        ? ReadonlyAccount<TAccountEventAuthority>
        : TAccountEventAuthority,
      TAccountProgram extends string
        ? ReadonlyAccount<TAccountProgram>
        : TAccountProgram,
      ...TRemainingAccounts,
    ]
  >;

export type SplitPosition2InstructionData = {
  discriminator: ReadonlyUint8Array;
  numerator: number;
};

export type SplitPosition2InstructionDataArgs = { numerator: number };

export function getSplitPosition2InstructionDataEncoder(): FixedSizeEncoder<SplitPosition2InstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["numerator", getU32Encoder()],
    ]),
    (value) => ({ ...value, discriminator: SPLIT_POSITION2_DISCRIMINATOR }),
  );
}

export function getSplitPosition2InstructionDataDecoder(): FixedSizeDecoder<SplitPosition2InstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["numerator", getU32Decoder()],
  ]);
}

export function getSplitPosition2InstructionDataCodec(): FixedSizeCodec<
  SplitPosition2InstructionDataArgs,
  SplitPosition2InstructionData
> {
  return combineCodec(
    getSplitPosition2InstructionDataEncoder(),
    getSplitPosition2InstructionDataDecoder(),
  );
}

export type SplitPosition2AsyncInput<
  TAccountPool extends string = string,
  TAccountFirstPosition extends string = string,
  TAccountFirstPositionNftAccount extends string = string,
  TAccountSecondPosition extends string = string,
  TAccountSecondPositionNftAccount extends string = string,
  TAccountFirstOwner extends string = string,
  TAccountSecondOwner extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  /** The first position */
  firstPosition: Address<TAccountFirstPosition>;
  /** The token account for position nft */
  firstPositionNftAccount: Address<TAccountFirstPositionNftAccount>;
  /** The second position */
  secondPosition: Address<TAccountSecondPosition>;
  /** The token account for position nft */
  secondPositionNftAccount: Address<TAccountSecondPositionNftAccount>;
  /** Owner of first position */
  firstOwner: TransactionSigner<TAccountFirstOwner>;
  /** Owner of second position */
  secondOwner: TransactionSigner<TAccountSecondOwner>;
  eventAuthority?: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  numerator: SplitPosition2InstructionDataArgs["numerator"];
};

export async function getSplitPosition2InstructionAsync<
  TAccountPool extends string,
  TAccountFirstPosition extends string,
  TAccountFirstPositionNftAccount extends string,
  TAccountSecondPosition extends string,
  TAccountSecondPositionNftAccount extends string,
  TAccountFirstOwner extends string,
  TAccountSecondOwner extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: SplitPosition2AsyncInput<
    TAccountPool,
    TAccountFirstPosition,
    TAccountFirstPositionNftAccount,
    TAccountSecondPosition,
    TAccountSecondPositionNftAccount,
    TAccountFirstOwner,
    TAccountSecondOwner,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SplitPosition2Instruction<
    TProgramAddress,
    TAccountPool,
    TAccountFirstPosition,
    TAccountFirstPositionNftAccount,
    TAccountSecondPosition,
    TAccountSecondPositionNftAccount,
    TAccountFirstOwner,
    TAccountSecondOwner,
    TAccountEventAuthority,
    TAccountProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    firstPosition: { value: input.firstPosition ?? null, isWritable: true },
    firstPositionNftAccount: {
      value: input.firstPositionNftAccount ?? null,
      isWritable: false,
    },
    secondPosition: { value: input.secondPosition ?? null, isWritable: true },
    secondPositionNftAccount: {
      value: input.secondPositionNftAccount ?? null,
      isWritable: false,
    },
    firstOwner: { value: input.firstOwner ?? null, isWritable: false },
    secondOwner: { value: input.secondOwner ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.eventAuthority.value) {
    accounts.eventAuthority.value = await findEventAuthorityPda();
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("firstPosition", accounts.firstPosition),
      getAccountMeta(
        "firstPositionNftAccount",
        accounts.firstPositionNftAccount,
      ),
      getAccountMeta("secondPosition", accounts.secondPosition),
      getAccountMeta(
        "secondPositionNftAccount",
        accounts.secondPositionNftAccount,
      ),
      getAccountMeta("firstOwner", accounts.firstOwner),
      getAccountMeta("secondOwner", accounts.secondOwner),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getSplitPosition2InstructionDataEncoder().encode(
      args as SplitPosition2InstructionDataArgs,
    ),
    programAddress,
  } as SplitPosition2Instruction<
    TProgramAddress,
    TAccountPool,
    TAccountFirstPosition,
    TAccountFirstPositionNftAccount,
    TAccountSecondPosition,
    TAccountSecondPositionNftAccount,
    TAccountFirstOwner,
    TAccountSecondOwner,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type SplitPosition2Input<
  TAccountPool extends string = string,
  TAccountFirstPosition extends string = string,
  TAccountFirstPositionNftAccount extends string = string,
  TAccountSecondPosition extends string = string,
  TAccountSecondPositionNftAccount extends string = string,
  TAccountFirstOwner extends string = string,
  TAccountSecondOwner extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  /** The first position */
  firstPosition: Address<TAccountFirstPosition>;
  /** The token account for position nft */
  firstPositionNftAccount: Address<TAccountFirstPositionNftAccount>;
  /** The second position */
  secondPosition: Address<TAccountSecondPosition>;
  /** The token account for position nft */
  secondPositionNftAccount: Address<TAccountSecondPositionNftAccount>;
  /** Owner of first position */
  firstOwner: TransactionSigner<TAccountFirstOwner>;
  /** Owner of second position */
  secondOwner: TransactionSigner<TAccountSecondOwner>;
  eventAuthority: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  numerator: SplitPosition2InstructionDataArgs["numerator"];
};

export function getSplitPosition2Instruction<
  TAccountPool extends string,
  TAccountFirstPosition extends string,
  TAccountFirstPositionNftAccount extends string,
  TAccountSecondPosition extends string,
  TAccountSecondPositionNftAccount extends string,
  TAccountFirstOwner extends string,
  TAccountSecondOwner extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: SplitPosition2Input<
    TAccountPool,
    TAccountFirstPosition,
    TAccountFirstPositionNftAccount,
    TAccountSecondPosition,
    TAccountSecondPositionNftAccount,
    TAccountFirstOwner,
    TAccountSecondOwner,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): SplitPosition2Instruction<
  TProgramAddress,
  TAccountPool,
  TAccountFirstPosition,
  TAccountFirstPositionNftAccount,
  TAccountSecondPosition,
  TAccountSecondPositionNftAccount,
  TAccountFirstOwner,
  TAccountSecondOwner,
  TAccountEventAuthority,
  TAccountProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    firstPosition: { value: input.firstPosition ?? null, isWritable: true },
    firstPositionNftAccount: {
      value: input.firstPositionNftAccount ?? null,
      isWritable: false,
    },
    secondPosition: { value: input.secondPosition ?? null, isWritable: true },
    secondPositionNftAccount: {
      value: input.secondPositionNftAccount ?? null,
      isWritable: false,
    },
    firstOwner: { value: input.firstOwner ?? null, isWritable: false },
    secondOwner: { value: input.secondOwner ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("firstPosition", accounts.firstPosition),
      getAccountMeta(
        "firstPositionNftAccount",
        accounts.firstPositionNftAccount,
      ),
      getAccountMeta("secondPosition", accounts.secondPosition),
      getAccountMeta(
        "secondPositionNftAccount",
        accounts.secondPositionNftAccount,
      ),
      getAccountMeta("firstOwner", accounts.firstOwner),
      getAccountMeta("secondOwner", accounts.secondOwner),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getSplitPosition2InstructionDataEncoder().encode(
      args as SplitPosition2InstructionDataArgs,
    ),
    programAddress,
  } as SplitPosition2Instruction<
    TProgramAddress,
    TAccountPool,
    TAccountFirstPosition,
    TAccountFirstPositionNftAccount,
    TAccountSecondPosition,
    TAccountSecondPositionNftAccount,
    TAccountFirstOwner,
    TAccountSecondOwner,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type ParsedSplitPosition2Instruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    /** The first position */
    firstPosition: TAccountMetas[1];
    /** The token account for position nft */
    firstPositionNftAccount: TAccountMetas[2];
    /** The second position */
    secondPosition: TAccountMetas[3];
    /** The token account for position nft */
    secondPositionNftAccount: TAccountMetas[4];
    /** Owner of first position */
    firstOwner: TAccountMetas[5];
    /** Owner of second position */
    secondOwner: TAccountMetas[6];
    eventAuthority: TAccountMetas[7];
    program: TAccountMetas[8];
  };
  data: SplitPosition2InstructionData;
};

export function parseSplitPosition2Instruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSplitPosition2Instruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 9,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      firstPosition: getNextAccount(),
      firstPositionNftAccount: getNextAccount(),
      secondPosition: getNextAccount(),
      secondPositionNftAccount: getNextAccount(),
      firstOwner: getNextAccount(),
      secondOwner: getNextAccount(),
      eventAuthority: getNextAccount(),
      program: getNextAccount(),
    },
    data: getSplitPosition2InstructionDataDecoder().decode(instruction.data),
  };
}
